import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Package, Plus, Search, Edit, Trash2, Eye, X, Truck, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';

interface VanStock {
  id: number;
  vanId: number;
  vanName: string;
  productId: number;
  productName: string;
  quantity: number;
  minQuantity: number;
  lastUpdated?: string;
  notes?: string;
  isLowStock: boolean;
}

interface Van { id: number; plateNumber: string; driverName?: string; }
interface Product { id: number; name: string; }

const T = {
  title: "مخزون السيارات",
  subtitle: "إدارة مخزون المنتجات في سيارات التوزيع",
  createNew: "إضافة مخزون جديد",
  totalProducts: "إجمالي المنتجات",
  totalQuantity: "إجمالي الكميات",
  lowStock: "منتجات منخفضة",
  vanCount: "عدد السيارات",
  search: "بحث باسم المنتج أو السيارة...",
  loading: "جاري التحميل...",
  noStock: "لا يوجد مخزون",
  thProduct: "المنتج",
  thVan: "السيارة",
  thQuantity: "الكمية",
  thMinQuantity: "الحد الأدنى",
  thStatus: "الحالة",
  thLastUpdate: "آخر تحديث",
  thActions: "الإجراءات",
  edit: "تعديل",
  delete: "حذف",
  editStock: "تعديل المخزون",
  createStock: "إضافة مخزون جديد",
  lblVan: "السيارة",
  lblProduct: "المنتج",
  lblQuantity: "الكمية",
  lblMinQuantity: "الحد الأدنى",
  lblNotes: "ملاحظات",
  cancel: "إلغاء",
  update: "تحديث",
  create: "إضافة",
  close: "إغلاق",
  confirmDelete: "هل أنت متأكد من حذف هذا المخزون؟",
  updated: "تم تحديث المخزون بنجاح",
  created: "تم إضافة المخزون بنجاح",
  deleted: "تم حذف المخزون بنجاح",
  error: "حدث خطأ",
  inStock: "متوفر",
  lowStockLabel: "منخفض",
  outOfStock: "نافد",
  allVans: "جميع السيارات"
};

export default function VanStocksPage() {
  const [stocks, setStocks] = useState<VanStock[]>([]);
  const [vans, setVans] = useState<Van[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [vanFilter, setVanFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingStock, setEditingStock] = useState<VanStock | null>(null);
  const [form, setForm] = useState({ vanId: '', productId: '', quantity: '0', minQuantity: '0', notes: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [stocksRes, vansRes, productsRes] = await Promise.all([
        api.get('/api/vanstocks'),
        api.get('/api/vans'),
        api.get('/api/products?pageSize=1000')
      ]);
      if (stocksRes.data?.success) setStocks(stocksRes.data.data || []);
      if (vansRes.data?.success) {
        const vansData = vansRes.data.data;
        setVans(Array.isArray(vansData) ? vansData : (vansData.items || []));
      }
      if (productsRes.data?.success) {
        const prodsData = productsRes.data.data;
        setProducts(Array.isArray(prodsData) ? prodsData : (prodsData.items || []));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingStock(null);
    setForm({ vanId: '', productId: '', quantity: '0', minQuantity: '0', notes: '' });
    setShowModal(true);
  };

  const openEdit = (s: VanStock) => {
    setEditingStock(s);
    setForm({
      vanId: s.vanId.toString(),
      productId: s.productId.toString(),
      quantity: s.quantity.toString(),
      minQuantity: s.minQuantity.toString(),
      notes: s.notes || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        vanId: parseInt(form.vanId),
        productId: parseInt(form.productId),
        quantity: parseFloat(form.quantity),
        minQuantity: parseFloat(form.minQuantity),
        notes: form.notes
      };
      if (editingStock) {
        await api.put('/api/vanstocks/' + editingStock.id, payload);
        alert(T.updated);
      } else {
        await api.post('/api/vanstocks', payload);
        alert(T.created);
      }
      setShowModal(false);
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.message || T.error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(T.confirmDelete)) return;
    try {
      await api.delete('/api/vanstocks/' + id);
      loadData();
      alert(T.deleted);
    } catch (err: any) {
      alert(err.response?.data?.message || T.error);
    }
  };

  const filtered = stocks.filter(s => {
    const matchSearch = !search ||
      s.productName.toLowerCase().includes(search.toLowerCase()) ||
      s.vanName.toLowerCase().includes(search.toLowerCase());
    const matchVan = vanFilter === 'all' || s.vanId.toString() === vanFilter;
    return matchSearch && matchVan;
  });

  const totalProducts = stocks.length;
  const totalQuantity = stocks.reduce((sum, s) => sum + s.quantity, 0);
  const lowStockCount = stocks.filter(s => s.isLowStock).length;
  const vanCount = new Set(stocks.map(s => s.vanId)).size;
  const inputClass = 'w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 transition';

  const getStatusInfo = (s: VanStock) => {
    if (s.quantity === 0) return { label: T.outOfStock, color: 'bg-red-50 text-red-700 border-red-200' };
    if (s.isLowStock) return { label: T.lowStock, color: 'bg-yellow-50 text-yellow-700 border-yellow-200' };
    return { label: T.inStock, color: 'bg-green-50 text-green-700 border-green-200' };
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
            <Package className="w-6 h-6 text-white" />
          </div>
          <div>
            <span>{T.title}</span>
            <p className="text-sm text-gray-500 font-normal mt-1">{T.subtitle}</p>
          </div>
        </h1>
        <Button onClick={openCreate} className="bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg">
          <Plus className="w-5 h-5 ml-2" />
          <span>{T.createNew}</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center"><Package className="w-5 h-5 text-orange-600" /></div></div>
          <p className="text-sm text-gray-500">{T.totalProducts}</p>
          <p className="text-2xl font-bold text-gray-900">{totalProducts}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Package className="w-5 h-5 text-blue-600" /></div></div>
          <p className="text-sm text-gray-500">{T.totalQuantity}</p>
          <p className="text-2xl font-bold text-blue-700">{totalQuantity.toLocaleString()}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-red-600" /></div></div>
          <p className="text-sm text-gray-500">{T.lowStock}</p>
          <p className="text-2xl font-bold text-red-700">{lowStockCount}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center"><Truck className="w-5 h-5 text-purple-600" /></div></div>
          <p className="text-sm text-gray-500">{T.vanCount}</p>
          <p className="text-2xl font-bold text-purple-700">{vanCount}</p>
        </CardContent></Card>
      </div>

      <Card className="border-0 shadow-lg">
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input type="text" placeholder={T.search} value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 transition" />
            </div>
            <select value={vanFilter} onChange={(e) => setVanFilter(e.target.value)} className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500">
              <option value="all">{T.allVans}</option>
              {vans.map(v => <option key={v.id} value={v.id}>{v.plateNumber}</option>)}
            </select>
          </div>
          {loading ? (
            <div className="p-12 text-center"><Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto" /><p className="text-gray-500 mt-4">{T.loading}</p></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center"><Package className="w-16 h-16 text-gray-300 mx-auto mb-4" /><h3 className="text-lg font-semibold text-gray-900 mb-2">{T.noStock}</h3></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thProduct}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thVan}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thQuantity}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thMinQuantity}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thStatus}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thLastUpdate}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thActions}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((stock) => {
                    const status = getStatusInfo(stock);
                    return (
                      <tr key={stock.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                        <td className="py-4 px-5">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white"><Package className="w-5 h-5" /></div>
                            <p className="font-semibold text-gray-900">{stock.productName}</p>
                          </div>
                        </td>
                        <td className="py-4 px-5">
                          <div className="flex items-center gap-2">
                            <Truck className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-700 text-sm font-mono">{stock.vanName}</span>
                          </div>
                        </td>
                        <td className="py-4 px-5"><span className="font-bold text-lg text-gray-900">{stock.quantity.toLocaleString()}</span></td>
                        <td className="py-4 px-5"><span className="text-gray-600">{stock.minQuantity.toLocaleString()}</span></td>
                        <td className="py-4 px-5">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${status.color}`}>
                            {stock.isLowStock && <AlertTriangle className="w-3 h-3" />}
                            {!stock.isLowStock && stock.quantity > 0 && <CheckCircle2 className="w-3 h-3" />}
                            {status.label}
                          </span>
                        </td>
                        <td className="py-4 px-5"><span className="text-sm text-gray-600">{stock.lastUpdated ? new Date(stock.lastUpdated).toLocaleString('ar-EG') : '-'}</span></td>
                        <td className="py-4 px-5">
                          <div className="flex items-center gap-1">
                            <button onClick={() => openEdit(stock)} className="p-2 hover:bg-orange-50 rounded-lg text-gray-500 hover:text-orange-600 transition" title={T.edit}><Edit className="w-4 h-4" /></button>
                            <button onClick={() => handleDelete(stock.id)} className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition" title={T.delete}><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"><div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{editingStock ? T.editStock : T.createStock}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>{T.lblVan} *</Label>
                  <select value={form.vanId} onChange={(e) => setForm({...form, vanId: e.target.value})} required className={inputClass}>
                    <option value="">{"اختر السيارة"}</option>
                    {vans.map(v => <option key={v.id} value={v.id}>{v.plateNumber}</option>)}
                  </select>
                </div>
                <div><Label>{T.lblProduct} *</Label>
                  <select value={form.productId} onChange={(e) => setForm({...form, productId: e.target.value})} required className={inputClass}>
                    <option value="">{"اختر المنتج"}</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div><Label>{T.lblQuantity} *</Label><Input type="number" step="0.01" value={form.quantity} onChange={(e: any) => setForm({...form, quantity: e.target.value})} required className={inputClass} /></div>
                <div><Label>{T.lblMinQuantity}</Label><Input type="number" step="0.01" value={form.minQuantity} onChange={(e: any) => setForm({...form, minQuantity: e.target.value})} className={inputClass} /></div>
                <div className="md:col-span-2"><Label>{T.lblNotes}</Label><textarea value={form.notes} onChange={(e: any) => setForm({...form, notes: e.target.value})} className={inputClass} rows={2} /></div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">{T.cancel}</Button>
                <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700">{editingStock ? T.update : T.create}</Button>
              </div>
            </form>
          </div></Card>
        </div>
      )}
    </div>
  );
}
